import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertCircle, RefreshCcw, Home } from 'lucide-react'

interface Props {
  children: ReactNode
  /** Optional name shown in the error UI, e.g. "Flow Builder" */
  featureName?: string
  /**
   * Inline fallback instead of the full-page error screen — for wrapping one
   * section/card so a crash there doesn't blank the whole page.
   */
  fallback?: (reset: () => void, error: Error | null) => ReactNode
  /** When this value changes, a caught error is cleared (e.g. new data/params). */
  resetKey?: unknown
}

interface State {
  hasError: boolean
  error: Error | null
}

export class FeatureErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log to console in dev; a production app would send this to Sentry/etc.
    console.error(`[FeatureErrorBoundary${this.props.featureName ? `: ${this.props.featureName}` : ''}]`, error, info.componentStack)
  }

  componentDidUpdate(prev: Props) {
    if (this.state.hasError && !Object.is(prev.resetKey, this.props.resetKey)) {
      this.setState({ hasError: false, error: null })
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null })
  }

  private handleGoHome = () => {
    window.location.href = '/dashboard'
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children
    }

    if (this.props.fallback) {
      return this.props.fallback(this.handleReset, this.state.error)
    }

    const name = this.props.featureName ?? 'This section'

    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] px-6 text-center">
        <div className="rounded-2xl bg-destructive/10 p-4 mb-4">
          <AlertCircle className="h-10 w-10 text-destructive" />
        </div>
        <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
        <p className="text-sm text-muted-foreground max-w-md mb-1">
          {name} encountered an unexpected error. You can try reloading or go back to the dashboard.
        </p>
        {this.state.error && (
          <pre className="mt-2 max-w-lg text-xs text-muted-foreground bg-muted rounded-lg p-3 overflow-auto max-h-32 text-left w-full">
            {this.state.error.message}
          </pre>
        )}
        <div className="flex gap-3 mt-6">
          <button
            onClick={this.handleReset}
            className="inline-flex h-11 items-center gap-2 rounded-lg border border-clay-300 bg-white px-5 text-sm font-semibold text-clay-800 transition-all hover:border-primary hover:text-primary hover:ring-1 hover:ring-primary dark:border-clay-700 dark:bg-clay-700 dark:text-clay-100"
          >
            <RefreshCcw className="h-[1.125rem] w-[1.125rem]" />
            Try again
          </button>
          <button
            onClick={this.handleGoHome}
            className="inline-flex h-11 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-mild active:bg-primary-deep"
          >
            <Home className="h-[1.125rem] w-[1.125rem]" />
            Dashboard
          </button>
        </div>
      </div>
    )
  }
}
